import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { track } from '@amplitude/analytics-browser';
import { goTo } from '@tiendanube/nexo';
import nexo from 'App/nexoClient';
import { ADMIN_ROUTES, ROUTES, SUPPORT_EMAIL } from 'App/Routes/constants';
import { NextActionCode } from 'commons/types';

interface UseNextActionHandlerOptions {
  onboardingHandlers?: {
    handleIndividualOnboarding?: () => void;
    handleOpenUploadDocuments?: () => void;
    handleOpenExternalFormModal?: (isIndividual: boolean) => void;
  };
  trackEventName?: string;
}

export const useNextActionHandler = (
  options: UseNextActionHandlerOptions = {},
) => {
  const history = useHistory();
  const {
    onboardingHandlers,
    trackEventName = 'pagonube_action_required_click',
  } = options;

  const handleNavigateToOnboarding = useCallback(() => {
    track(trackEventName);
    history.push(ROUTES.ONBOARDING);
  }, [history, trackEventName]);

  const handleContactSupport = useCallback(() => {
    track('pagonube_contact_support_click');
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  }, []);

  const handleNavigateToPaymentMethods = useCallback(() => {
    goTo(nexo, ADMIN_ROUTES.PAYMENT_METHODS);
  }, []);
  const getHandlerForNextAction = useCallback(
    (nextActionCode?: NextActionCode): (() => void) => {
      if (!nextActionCode) {
        return handleNavigateToPaymentMethods;
      }
      const nextActionHandlers: Record<NextActionCode, () => void> = {
        [NextActionCode.COMPLETE_FORM]:
          onboardingHandlers?.handleIndividualOnboarding ??
          handleNavigateToOnboarding,
        [NextActionCode.UPLOAD_DOCUMENTS]:
          onboardingHandlers?.handleOpenUploadDocuments ??
          handleNavigateToOnboarding,
        [NextActionCode.COMPLETE_EXTERNAL_FORM_INDIVIDUAL]:
          onboardingHandlers?.handleOpenExternalFormModal
            ? () => onboardingHandlers.handleOpenExternalFormModal!(true)
            : handleNavigateToOnboarding,
        [NextActionCode.COMPLETE_EXTERNAL_FORM_COMPANY]:
          onboardingHandlers?.handleOpenExternalFormModal
            ? () => onboardingHandlers.handleOpenExternalFormModal!(false)
            : handleNavigateToOnboarding,
        [NextActionCode.CONTACT_SUPPORT]: handleContactSupport,
      };

      return nextActionHandlers[nextActionCode] ?? handleNavigateToOnboarding;
    },
    [
      handleContactSupport,
      handleNavigateToOnboarding,
      handleNavigateToPaymentMethods,
      onboardingHandlers,
    ],
  );

  return {
    getHandlerForNextAction,
    handleContactSupport,
    handleNavigateToOnboarding,
    handleNavigateToPaymentMethods,
  };
};

export default useNextActionHandler;
